<template>
<div>

	<confirm-copy-notification :show="copyNotificationVisible"></confirm-copy-notification>
	<not-authorized-notification :show="notAuthorizedNotificationVisible" :auth="props.auth"></not-authorized-notification>

	<div class="mb-4">

		<div class="mt-1 relative rounded-md shadow-sm">
			<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">

				<svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd"
						  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
						  clip-rule="evenodd"></path>
				</svg>
			</div>
			<input v-model="searchQuery"
				   name="searchQuery" id="searchQuery"
				   class="h-10 focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md shadow"
				   placeholder="Search..."
				   data-heap="search-for-icon">
		</div>

	</div>
	<div class="bg-white rounded-lg shadow px-5 py-6 sm:px-6">
		<RecycleScroller page-mode
			v-if="processedList.length > 0"
			class="scroller"
			:items="processedList"
			:item-size="calculateItemHeight()"
			key-field="key"
			v-slot="{ item, index }"
			:buffer="200"
		>
			<template v-if="item.type === 'header'">
				<div :key="item.key" class="w-full col-span-full">
					<hr v-if="item.showHr" class="pb-3 my-3 border-t border-gray-200">
					<div v-else
						class="ring-1 ring-gray-900/10 bg-slate-100 p-1 px-4 rounded-full text-xs flex mb-4 m-1 w-fit mx-auto text-gray-700">
						<svg class="size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 256 256">
							<path fill="#F4B400"
								  d="M0 30C0 13.431 13.431 0 30 0h196c16.569 0 30 13.431 30 30v196c0 16.569-13.431 30-30 30H30c-16.569 0-30-13.431-30-30V30Z"></path>
							<path fill="#fff" d="M28 71h200v114H28V71Z"></path>
						</svg>
						<span class="pl-2">These icons are designed to be pasted directly into Google Slides. If you want the actual SVG, check the source link above.</span>
					</div>
					<h2 class="font-bold font-funnel text-gray-900 pb-3 text-2xl tracking-wide">{{ item.classification }}</h2>
				</div>
			</template>
			<template v-else-if="item.type === 'icon-row'">
				<ul role="list" class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-12" :key="item.key">
					<li v-for="icon in item.icons" :key="icon.id"
						@click="copyToClipboard(icon)"
						class="relative flex flex-col-reverse group cursor-pointer"
						data-heap="select-icon"
					>
						<h3 class="text-center max-h-4 text-xs group-hover:text-gray-800 px-1"
							:id="icon.short_name"
							:class="searchQuery.length > 0 ? 'text-gray-500' : 'text-transparent'"
							:title="icon.formal_name"
						>{{ icon.formal_name }}</h3>
						<div class="relative mb-1 h-20 flex items-center justify-center">
							<div class="icon-wrapper group p-2 rounded-md hover:bg-gray-100 flex items-center justify-center w-full h-full">
								<img :src="icon.url" class="w-6 h-6 transition-transform duration-150 ease-in-out group-hover:scale-110" :alt="icon.formal_name" loading="lazy" />
							</div>
						</div>
					</li>
				</ul>
			</template>

		</RecycleScroller>

		<div v-else class="text-center text-gray-700 my-4">
			<h2 v-if="searchQuery">Sorry, no icons match your search term "{{ searchQuery }}".</h2>
			<h2 v-else>Loading icons or this collection is empty.</h2>
		</div>
	</div>
</div>
</template>

<script setup>
import {computed, ref, watch, onMounted, onUnmounted} from "vue";
import ConfirmCopyNotification from "@/Components/ConfirmCopyNotification";
import NotAuthorizedNotification from "@/Components/NotAuthorizedToCopyNotification.vue";
import { RecycleScroller } from 'vue-virtual-scroller'; // Import the scroller
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'; // Import default CSS


const props = defineProps({
	icons: Object,
	auth: Object
})

const searchQuery = ref('');
const copyNotificationVisible = ref(false);
const notAuthorizedNotificationVisible = ref(false);
const windowWidth = ref(window.innerWidth); // For responsive item height calculation



// --- Responsive Column Count ---
const columns = computed(() => {
	if (windowWidth.value >= 1024) return 12; // lg
	if (windowWidth.value >= 768) return 6;  // md
	if (windowWidth.value >= 640) return 3;  // sm
	return 2; // default
});

// --- Item Height Calculation ---
// This needs to be dynamic based on columns or a reasonable estimate.
// Height = Icon container height + text height + margins/padding
const ICON_ROW_BASE_HEIGHT = 120; // Approx height for icon (h-20 = 80px) + text + padding
const HEADER_HEIGHT = 50; // Approx height for header + hr + padding

function calculateItemHeight() {
	// For simplicity, we'll use a fixed average height.
	// A more accurate approach would calculate based on the number of columns
	// for 'icon-row' type items, but RecycleScroller prefers fixed heights.
	// Let's estimate based on the icon row height as it's the most common.
	return ICON_ROW_BASE_HEIGHT;
}

// --- Processed List for Virtual Scroller ---
const processedList = computed(() => {
	const query = searchQuery.value.toLowerCase().trim();
	// Use debouncedSearchQuery.value if using debounce

	// 1. Filter icons based on search query
	const filteredIcons = query
		? props.icons.filter(icon =>
			icon.formal_name.toLowerCase().includes(query) ||
			icon.short_name.toLowerCase().includes(query) ||
			icon.classification.toLowerCase().includes(query) // Optional: search classification too
		)
		: [...props.icons]; // Use a copy if no query

	if (filteredIcons.length === 0) {
		return []; // Return empty if no icons match
	}

	// 2. Group filtered icons by classification
	const grouped = filteredIcons.reduce((acc, icon) => {
		const classification = icon.classification || 'Unclassified'; // Ensure classification exists
		if (!acc[classification]) {
			acc[classification] = [];
		}
		acc[classification].push(icon);
		return acc;
	}, {});

	// 3. Sort classifications alphabetically
	const sortedClassifications = Object.keys(grouped).sort();

	// 4. Create the flat list with headers and icon rows for the scroller
	const list = [];
	let firstHeader = true;
	const numColumns = columns.value; // Get current column count

	sortedClassifications.forEach(classification => {
		// Add header item
		list.push({
			key: `header_${classification}`, // Unique key for header
			type: 'header',
			classification: classification,
			showHr: !firstHeader // Add HR except for the very first header
		});
		firstHeader = false;

		// Chunk icons into rows based on the current number of columns
		const iconsInGroup = grouped[classification];
		for (let i = 0; i < iconsInGroup.length; i += numColumns) {
			const rowIcons = iconsInGroup.slice(i, i + numColumns);
			list.push({
				key: `row_${classification}_${i}`, // Unique key for the row
				type: 'icon-row',
				icons: rowIcons
			});
		}
	});

	return list;
});


// --- Handle Window Resize for Columns ---
const updateWidth = () => {
	windowWidth.value = window.innerWidth;
};

onMounted(() => {
	window.addEventListener('resize', updateWidth);
	// Initial calculation
	updateWidth();
});

onUnmounted(() => {
	window.removeEventListener('resize', updateWidth);
});


const isSearchResultsBlank = computed(() => {
	return Object.keys(iconObject.value).length === 0
});



let copyElement = ref('');


document.addEventListener('copy', function(e){

	// only intercept if we're trying to copy something
	if(copyElement.value !== '') {

		// pull out the actual content we want from the json
		let element = copyElement.value['clipboard']['gslides'];

		// loop through it and add to the clipboard for each one
		for(const type in element) {
			e.clipboardData.setData(type, element[type]);
		}
		console.log("added!");

		e.preventDefault(); // default behaviour is to copy any selected text

		copyNotificationVisible.value = true;
		setTimeout(() => copyNotificationVisible.value = false, 6000)

		// now clear the copyElement so we're ready for the next copy
		copyElement.value = '';

	}
});

function copyToClipboard(icon) {


	// check if user is authorized to copy this icon
	if(icon.premium) { // if this is a premium icon
		if(!props.auth.user.isSubscribed && !props.auth.user.onTrial) {
			// you are not a premium user and this is a premium icon
			notAuthorizedNotificationVisible.value = true;
			setTimeout(() => notAuthorizedNotificationVisible.value = false, 8000)
		} else {
			// its premium but you're authorized
			copyElement.value = icon;
			document.execCommand("copy");
			recordAction(icon);
		}
	} else {
		// set the selected icon to copyElement so we can use it later
		copyElement.value = icon;
		document.execCommand("copy");
		recordAction(icon);
	}


}

function recordAction(icon) {
	axios.post('/icons/recordAction', {
		icon: icon.id,
	});
}

</script>

<style scoped>
.scroller {
	height: 100%; /* Adjust height as needed, vh units are often good */

}

/* Add styles for your icon wrapper and corners if you keep them */
.icon-wrapper {
	position: relative;
	/* Add other base styles */
}

/* Example corner styles (if kept) */
.corner {
	position: absolute;
	width: 8px;
	height: 8px;
	border-color: transparent; /* Default */
	border-style: solid;
	opacity: 0;
	transition: opacity 0.2s ease-in-out, border-color 0.2s ease-in-out;
}
.group:hover .corner {
	opacity: 1;
	border-color: theme('colors.indigo.500'); /* Example hover color */
}
.corner-top-left { top: -2px; left: -2px; border-width: 2px 0 0 2px; border-top-left-radius: 4px; }
.corner-top-right { top: -2px; right: -2px; border-width: 2px 2px 0 0; border-top-right-radius: 4px; }
.corner-bottom-left { bottom: -2px; left: -2px; border-width: 0 0 2px 2px; border-bottom-left-radius: 4px; }
.corner-bottom-right { bottom: -2px; right: -2px; border-width: 0 2px 2px 0; border-bottom-right-radius: 4px; }

/* Ensure images don't cause layout shifts */
img {
	display: block;
	max-width: 100%;
	height: auto;
}

/* Tailwind utility for text truncation */
.truncate {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>